import { useState, useRef, useEffect, useCallback } from "react";

const INTERSECTION_THRESHOLD = 0.1;
const ROOT_MARGIN = "50px";

/**
 * Custom React hook that tracks the visibility of course elements using Intersection Observer API.
 *
 * This hook provides functionality to observe DOM elements representing courses and maintain
 * a list of currently visible course IDs. It's designed to optimize performance by only
 * tracking elements that are actually visible in the viewport.
 *
 * @param {boolean} viewOnly - If true, disables the intersection observer functionality
 * @returns {Object} Hook return object
 * @returns {string[]} returns.visibleCoursesIds - Array of IDs for currently visible courses
 * @returns {Object} returns.coursesRef - Ref object containing references to course elements
 * @returns {Function} returns.coursesSetRef - Callback function to set refs for course elements
 *
 * @example
 * ```javascript
 * const { visibleCoursesIds, coursesSetRef } = useVisibleCourses(false);
 *
 * // Use in component render
 * <div ref={(el) => coursesSetRef(courseId, el)} data-id={courseId}>
 *   Course content
 * </div>
 * ```
 *
 * @requires INTERSECTION_THRESHOLD - Global constant defining intersection threshold
 * @requires ROOT_MARGIN - Global constant defining root margin for intersection observer
 */
const useVisibleCourses = viewOnly => {
  const [visibleCoursesIds, setVisibleCoursesIds] = useState([]);
  const coursesRef = useRef({});
  const courseObserverRef = useRef(null);

  // Create a function to observe an element
  const observeElement = useCallback((element, observer) => {
    if (element && element.nodeType === Node.ELEMENT_NODE && observer) {
      observer.observe(element);
    }
  }, []);

  // Create a function to unobserve an element
  const unobserveElement = useCallback((element, observer) => {
    if (element && element.nodeType === Node.ELEMENT_NODE && observer) {
      observer.unobserve(element);
    }
  }, []);

  // Initialize Intersection Observers
  useEffect(() => {
    if (viewOnly) return;
    // Course Observer - tracks individual course items
    courseObserverRef.current = new IntersectionObserver(
      entries => {
        const visibleIds = [];
        const hiddenIds = [];

        entries.forEach(entry => {
          const elementId = entry.target.getAttribute("data-id");
          if (elementId) {
            if (entry.isIntersecting) {
              visibleIds.push(elementId);
            } else {
              hiddenIds.push(elementId);
            }
          }
        });

        if (visibleIds.length > 0 || hiddenIds.length > 0) {
          setVisibleCoursesIds(prevIds => {
            const updatedIds = new Set(prevIds);
            visibleIds.forEach(id => updatedIds.add(id));
            hiddenIds.forEach(id => updatedIds.delete(id));
            return Array.from(updatedIds);
          });
        }
      },
      {
        threshold: INTERSECTION_THRESHOLD,
        rootMargin: ROOT_MARGIN,
      }
    );

    return () => {
      if (courseObserverRef.current) {
        courseObserverRef.current.disconnect();
      }
    };
  }, []);

  // Create stable setRef functions
  const coursesSetRef = useCallback(
    (index, element) => {
      if (element) {
        coursesRef.current[index] = element;
        // Immediately observe the element when it's added
        if (courseObserverRef.current) {
          observeElement(element, courseObserverRef.current);
        }
      } else {
        // Unobserve and remove the element when it's removed
        const oldElement = coursesRef.current[index];
        if (oldElement && courseObserverRef.current) {
          unobserveElement(oldElement, courseObserverRef.current);
        }
        delete coursesRef.current[index];
      }
    },
    [observeElement, unobserveElement]
  );

  return {
    visibleCoursesIds,
    coursesRef,
    // Provide the setRef functions separately to avoid ref structure issues
    coursesSetRef,
  };
};

export default useVisibleCourses;
